import { Box, Button, Center, Flex, IconButton, Image, SimpleGrid, Text, useDisclosure } from '@chakra-ui/react';
import bgBrandModelImage from 'assets/background/brand_modal.png';
import { ReactComponent as NewTabIcon } from 'assets/icons/new-tab.svg';
import BrandKitImage from 'assets/images/brand_kit.png';
import { BRAND_REDIRECT_LINK, UPGRADE_REDIRECT_BIZ_LINK } from 'constants/links';
import { CLICK_ON_PAID_FEATURE, IMAGE_CREATED, UPGRADE } from 'constants/mixpanel';
import { useChameleon } from 'libs/third-party/Chameleon';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { CommonGatedUpgradeModal } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal';
import { ModalUpgradeTypeEnum } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal.types';
import { WidgetDragDropWrapper } from 'modules/Editor/components/WidgetDragDrop';
import { getInitialWidgetPosition } from 'modules/Editor/store';
import { PermissionBadge, UserTypes } from 'modules/User';
import { memo, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetType } from 'types/widget.types';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';
import { generateDefaultData as generateImageData } from 'widgets/ImageWidget/ImageWidget.helpers';
import { NewWidget } from 'widgets/Widget.types';

import { LoadingOverlay } from '../../InfiniteGrid';
import { THUMBNAIL_HEIGHT, THUMBNAIL_WIDTH } from '../../InfiniteGrid/InfiniteGrid.config';
import { BrandKitRedirectButton } from './BrandKitRedirectButton';
import { Loading } from './Loading';
import { SimpleBrandKitButton } from './SimpleEditBrandKitButton';

interface BrandLogosProps {
  hasBrandEditPermission: boolean;
  serializedLogoData: {
    previewHeight: number;
    tags: string;
    previewURL: string;
  }[];
  onLogoClick: (widget: NewWidget | NewWidget[]) => void;
  hasLogoPermission: boolean;
  disableDragDrop?: boolean;
  showHeading?: boolean;
  showNoLogoView?: boolean;
  isLoading?: boolean;
}

export const BrandLogos = memo(
  ({
    hasBrandEditPermission,
    disableDragDrop,
    serializedLogoData,
    onLogoClick,
    hasLogoPermission,
    showHeading,
    showNoLogoView,
    isLoading = false,
  }: BrandLogosProps) => {
    const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.brandKit' });
    const showLogos = !!serializedLogoData.length;

    const createWidgetData = useCallback((item: unknown, imageDom: HTMLImageElement | undefined | null) => {
      const typedItem = item as typeof serializedLogoData[number];

      const { naturalHeight = THUMBNAIL_HEIGHT, naturalWidth = THUMBNAIL_WIDTH } = imageDom || {};

      const { top, left, infographWidth, infographHeight } = getInitialWidgetPosition();
      const widgetData = generateImageData(
        typedItem.previewURL, // image url
        { width: naturalWidth, height: naturalHeight }, // image size
        { width: infographWidth, height: infographHeight }, // infograph size
        top,
        left,
      );

      return { ...widgetData, ...{ shouldUpdateImageSize: true } };
    }, []);

    const handleLogoClick = useCallback(
      (
        item: {
          previewHeight: number;
          tags: string;
          previewURL: string;
        },
        imageDom: HTMLImageElement | undefined | null,
      ) =>
        new Promise((resolve) => {
          const widget = createWidgetData(item, imageDom);

          onLogoClick(widget);

          Mixpanel.track(IMAGE_CREATED.eventName, {
            from: IMAGE_CREATED.from.Brand,
          });
          resolve(widget);
        }),
      [createWidgetData, onLogoClick],
    );

    return (
      <Flex flexDir='column' h='full'>
        {showHeading && (
          <Flex alignItems='center' justifyContent='space-between' mt={4} mb={showLogos ? 2 : 4}>
            <Text data-testid='logo-heading-txt' fontSize='sm' fontWeight='semibold' color='font.500'>
              {t('logos.heading')}
            </Text>
            <SimpleBrandKitButton ariaLabel={t('editLogos')} hasEditPermission={hasBrandEditPermission} />
          </Flex>
        )}
        {!showLogos &&
          !isLoading &&
          (showNoLogoView ? (
            <NoLogoView buttonLabel={t('uploadLogosToBrandKit')} hasBrandEditPermission={hasBrandEditPermission} />
          ) : (
            <BrandKitRedirectButton
              title={t('addLogo')}
              text={t('addLogoText')}
              hasBrandEditPermission={hasBrandEditPermission}
            />
          ))}

        {isLoading && <Loading />}
        {showLogos && !isLoading && (
          <LogoGridView
            serializedLogoData={serializedLogoData}
            hasLogoPermission={hasLogoPermission}
            onLogoClick={handleLogoClick}
            createWidgetData={createWidgetData}
            disableDragDrop={disableDragDrop}
          />
        )}
      </Flex>
    );
  },
);

BrandLogos.displayName = 'BrandLogos';

interface LogoGridViewProps {
  serializedLogoData: {
    previewHeight: number;
    tags: string;
    previewURL: string;
  }[];
  hasLogoPermission: boolean;
  onLogoClick: (
    item: {
      previewHeight: number;
      tags: string;
      previewURL: string;
    },
    imageDom: HTMLImageElement | undefined | null,
  ) => Promise<unknown>;
  createWidgetData: (item: unknown, imageDom: HTMLImageElement | undefined | null) => NewWidget;
  disableDragDrop?: boolean;
}
export const LogoGridView = ({
  serializedLogoData,
  hasLogoPermission,
  disableDragDrop,
  onLogoClick,
  createWidgetData,
}: LogoGridViewProps) => {
  return (
    <Box data-testid='logo-grid-view' mt={3}>
      <SimpleGrid columns={3} gap={2}>
        {serializedLogoData.map((data, index) => (
          <Logo
            key={`${index}-${data.previewURL}`}
            data={data}
            onItemClick={onLogoClick}
            disableDragDrop={disableDragDrop}
            createWidgetData={createWidgetData}
            hasLogoPermission={hasLogoPermission}
          />
        ))}
      </SimpleGrid>
    </Box>
  );
};

interface LogoProps {
  data: {
    previewHeight: number;
    tags: string;
    previewURL: string;
  };
  onItemClick: (
    item: {
      previewHeight: number;
      tags: string;
      previewURL: string;
    },
    imageDom: HTMLImageElement | undefined | null,
  ) => Promise<unknown>;
  disableDragDrop?: boolean;
  createWidgetData: (item: unknown, imageDom: HTMLImageElement | undefined | null) => NewWidget;
  hasLogoPermission: boolean;
}

const BRAND_EVENT_DATA = {
  event_name: IMAGE_CREATED.eventName,
  properties: {
    from: IMAGE_CREATED.from.Brand,
  },
};

const DRAG_IMAGE_OFFSET = { x: 78, y: 32 };
const LOGO_SIZE = 94;
const WRAPPER_CONTENT_PROPS = { height: '100%', width: '100%', padding: 0 };

const Logo = ({ data, onItemClick, createWidgetData, disableDragDrop, hasLogoPermission }: LogoProps) => {
  const imageRef = useRef<HTMLImageElement>(null);

  const [isAddingImage, setIsAddingImage] = useState(false);
  const { trackChameleon } = useChameleon();
  const { isOpen: isUpgradeModal, onOpen: onOpenUpgradeModal, onClose: onCloseUpgradeModal } = useDisclosure();

  if (!data) return null;

  const onClick = () => {
    setIsAddingImage(true);
    onItemClick(data, imageRef.current).finally(() => {
      setIsAddingImage(false);
    });
  };

  const onClickUpgrade = () => {
    Mixpanel.track(UPGRADE.eventName, {
      Feature: UPGRADE.feature.BrandKitLogo,
      plan: UserTypes.Business,
    });
    trackChameleon(UPGRADE.eventName);

    setAnalyticsUpgradeFeatureProp(UPGRADE.feature.BrandKitLogo);

    window.open(UPGRADE_REDIRECT_BIZ_LINK, '_self');
  };

  const onClickUpgradeModal = () => {
    onOpenUpgradeModal();
    Mixpanel.track(CLICK_ON_PAID_FEATURE.eventName, {
      Feature: UPGRADE.feature.BrandKitLogo,
      plan: UserTypes.Business,
    });
  };

  const onCreateWidgetData = () =>
    typeof createWidgetData === 'function' ? createWidgetData(data, imageRef.current) : undefined;

  return (
    <Flex
      px={0}
      justifyContent={'center'}
      alignItems={'center'}
      position='relative'
      role='group'
      height={LOGO_SIZE + 'px'}
      minH={LOGO_SIZE + 'px'}
      width={LOGO_SIZE + 'px'}
      minW={LOGO_SIZE + 'px'}
    >
      <WidgetDragDropWrapper
        isDraggable={hasLogoPermission && !disableDragDrop}
        widgetData={onCreateWidgetData()}
        wrapperContentProps={WRAPPER_CONTENT_PROPS}
        type={WidgetType.Image}
        dragImage={
          <Flex w={156} h='100px'>
            <Center>
              <Image src={data.previewURL} maxH='100%' />
            </Center>
          </Flex>
        }
        dragImageOffset={DRAG_IMAGE_OFFSET}
        trackingEventData={BRAND_EVENT_DATA}
      >
        <IconButton
          aria-label={data.tags}
          w='full'
          h='full'
          bg='gray.100'
          onClick={hasLogoPermission ? onClick : onClickUpgradeModal}
          variant={'icon-btn-side-menu'}
          p={2}
          data-testid='brand-logo'
        >
          <Image
            ref={imageRef}
            src={data.previewURL}
            alt=''
            role='presentation'
            maxH='100%'
            borderRadius='sm'
            pointerEvents='none'
          />
        </IconButton>
      </WidgetDragDropWrapper>
      {isAddingImage && <LoadingOverlay />}
      {!hasLogoPermission && (
        <>
          <CommonGatedUpgradeModal
            isOpen={isUpgradeModal}
            onClose={onCloseUpgradeModal}
            onClickUpgrade={onClickUpgrade}
            upgradePlan={UserTypes.Business}
            modalImg={BrandKitImage}
            modalType={ModalUpgradeTypeEnum.BrandKit}
          />
          <PermissionBadge permission={UserTypes.Business} position='absolute' top={-2} right={1} />
        </>
      )}
    </Flex>
  );
};

interface NoLogoViewProps {
  buttonLabel: string;
  hasBrandEditPermission: boolean;
}

export const NoLogoView = ({ buttonLabel, hasBrandEditPermission }: NoLogoViewProps) => {
  const { t } = useTranslation('auto_brand', { useSuspense: false, keyPrefix: 'modals.autoBrand' });
  return (
    <Flex
      direction='column'
      h='full'
      alignItems='center'
      justifyContent='center'
      paddingX={8}
      data-testid='brand-no-logo-view'
    >
      <Image src={bgBrandModelImage} alt='Brand Model' />
      <Text fontSize='md' fontWeight='semibold' marginTop={4}>
        {t('lookProfessional')}
      </Text>
      <Text fontSize='sm' marginTop={4}>
        {t('brandConsistency')}
      </Text>
      <Text fontSize='sm' textAlign='center'>
        {t('saveTime')}
      </Text>
      {hasBrandEditPermission && (
        <Button
          as='a'
          href={BRAND_REDIRECT_LINK}
          target='_blank'
          rel='noopener noreferrer'
          leftIcon={<NewTabIcon />}
          size='md'
          variant='upgrade'
          marginTop={4}
        >
          {buttonLabel}
        </Button>
      )}
      {!hasBrandEditPermission && (
        <Text fontSize='sm' fontWeight='semibold' textAlign='center' marginTop={4}>
          {t('askYourAdmin')}
        </Text>
      )}
    </Flex>
  );
};
